// Admin User Types

export type AdminRole = 'SUPER_ADMIN' | 'ADMIN' | 'SUPPORT_AGENT';

export interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: AdminRole;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  failedLoginAttempts?: number;
  lockedUntil?: string | null;
}

export interface CreateAdminUserDto {
  email: string;
  password: string;
  name: string;
  role: AdminRole;
}

export interface AdminUserResponse {
  id: number;
  email: string;
  name: string;
  role: AdminRole;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

// Helper to get current user from localStorage
export function getCurrentUser(): AdminUser | null {
  try {
    const userStr = localStorage.getItem('adminUser');
    if (userStr) {
      return JSON.parse(userStr) as AdminUser;
    }
  } catch {
    return null;
  }
  return null;
}

// Check if user has required role
export function hasRole(requiredRoles: AdminRole[]): boolean {
  const user = getCurrentUser();
  if (!user) return false;
  return requiredRoles.includes(user.role);
}

// Check if user is super admin
export function isSuperAdmin(): boolean {
  return hasRole(['SUPER_ADMIN']);
}

// Check if user is admin or higher
export function isAdmin(): boolean {
  return hasRole(['SUPER_ADMIN', 'ADMIN']);
}

// Role display labels
export const roleLabels: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  SUPPORT_AGENT: 'Support Agent',
};

// Role badge colors
export const roleColors: Record<AdminRole, string> = {
  SUPER_ADMIN: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  ADMIN: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  SUPPORT_AGENT: 'bg-primary/20 text-primary border-primary/30',
};
